/**
 * NOMMER SA VILLE — récompense, relance, et droit d'entrée au classement.
 *
 * ══ Le défaut, vu par Benoit sur le classement en ligne ══════════════════════
 *
 * « J'ai l'impression qu'il y a beaucoup de doublons. » Il y en a : **894 villes
 * classées, presque toutes appelées « Ma ville ».** Le classement affiche donc dix lignes
 * identiques, et le joueur ne peut y reconnaître ni la sienne ni personne.
 *
 * Ce n'est pas un défaut d'affichage. Un classement dont les entrées n'ont pas de nom
 * n'est pas un classement : c'est une liste de nombres.
 *
 * ══ La décision, et pourquoi elle est bonne ═════════════════════════════════
 *
 * « Il faut forcer à renommer sa ville, en récompense de gemmes, plusieurs fois tant que
 * ce n'est pas fait. Seules les villes renommées rentrent dans le classement. »
 * (Benoit, 22 août.)
 *
 * Elle règle le problème par le bon bout — **la porte d'entrée plutôt que le filtre
 * d'affichage** — et elle tient les trois règles du projet :
 *
 * · **le §14 n'est pas touché.** Renommer ne donne aucune production, seulement le droit
 *   d'apparaître. Un joueur qui refuse joue exactement le même jeu, aussi vite ;
 * · **G§0 Loi 1 tient aussi.** La récompense est en gemmes, mais elle s'obtient par un
 *   geste gratuit : personne ne paie pour entrer au classement ;
 * · **rien n'est perdu.** Une ville non nommée continue d'être sauvegardée, partagée,
 *   visitée. Elle n'est pas classée, c'est tout — et ça se dit à l'écran.
 *
 * ⚠️ Et surtout : **on ne touche PAS à `total_production`.** La tentation était d'arrêter
 * de l'écrire pour les villes sans nom — le classement les aurait écartées tout seul.
 * Ç'aurait été un désastre : la purge (ROADMAP 0.4) tient une production nulle pour « ville
 * jamais jouée » et **supprime**. On aurait effacé les villes de vrais joueurs au seul
 * motif qu'ils n'avaient pas choisi de nom. Le filtre vit donc dans la LECTURE du
 * classement, jamais dans l'écriture de la colonne.
 */

/**
 * Gemmes offertes au premier renommage.
 *
 * Vingt-cinq : le prix d'une accélération, donc quelque chose qu'on peut dépenser tout de
 * suite. Une récompense qu'on ne peut rien acheter avec ne se ressent pas — et c'est le
 * ressenti qui déclenche le geste, pas le nombre.
 */
export const RENAME_REWARD_GEMS = 25;

/**
 * Identifiant de l'octroi, rangé dans `claimedMilestones`.
 *
 * On réutilise la liste des jalons encaissés plutôt que d'ajouter un drapeau : elle est
 * déjà persistée, déjà relue à la sauvegarde, et **déjà idempotente par construction**.
 * Un second octroi est impossible sans qu'on ait à y penser.
 */
export const RENAME_CLAIM_ID = 'city-renamed';

/**
 * Ce nom est-il celui que le jeu a donné par défaut ?
 *
 * ⚠️ La liste des noms par défaut est PASSÉE, jamais lue ici : `src/core` n'a pas le droit
 * de connaître `src/i18n` (règle d'architecture, appliquée par ESLint). L'appelant la
 * dérive des dictionnaires — donc **toutes les langues**, pas seulement celle du joueur.
 *
 * C'est nécessaire : une ville créée en anglais s'appelle « My city », et un joueur
 * français ne doit pas la voir passer au classement sous prétexte que son propre défaut
 * s'écrit autrement. Les deux existent déjà en base, mesuré.
 *
 * La comparaison ignore la casse et les espaces de bord — « ma ville  » n'est pas un choix.
 */
export function isDefaultCityName(name: string, defaults: readonly string[]): boolean {
  const normalized = name.trim().toLocaleLowerCase();
  if (normalized === '') return true;
  return defaults.some((entry) => entry.trim().toLocaleLowerCase() === normalized);
}

/**
 * Délai entre deux relances.
 *
 * ⚠️ **Six heures, pas une semaine** — et l'écart avec `ACCOUNT_PROMPT_COOLDOWN_MS` est
 * délibéré. Créer un compte engage le joueur : le lui redemander trop souvent est du
 * harcèlement. Nommer sa ville ne l'engage à rien, prend cinq secondes, rapporte des
 * gemmes, et **conditionne son entrée au classement** — l'insistance est ici un service.
 *
 * Benoit a demandé « plusieurs fois tant que ce n'est pas fait ». Six heures donne deux à
 * trois rappels par journée de jeu, ce qui insiste sans occuper l'écran.
 */
export const RENAME_NUDGE_COOLDOWN_MS = 6 * 60 * 60 * 1000;

/**
 * Faut-il relancer le joueur maintenant ?
 *
 * Rend `false` dès que la ville porte un nom choisi : la relance disparaît au premier
 * succès, et ne revient jamais. Un rappel pour quelque chose de déjà fait est le genre de
 * détail qui fait douter du reste du jeu.
 */
export function shouldNudgeRename(
  name: string,
  defaults: readonly string[],
  lastNudged: number | null,
  now: number,
): boolean {
  if (!isDefaultCityName(name, defaults)) return false;
  if (lastNudged === null) return true;
  /*
    Une horloge locale peut reculer — fuseau, correction NTP, appareil remis à l'heure. Un
    « dernier rappel » situé dans le futur ne doit pas faire taire la relance pour
    toujours : on la traite alors comme jamais faite. Même raisonnement que
    `shouldPromptAccount`, et pour la même raison.
  */
  if (lastNudged > now) return true;
  return now - lastNudged >= RENAME_NUDGE_COOLDOWN_MS;
}
